import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import createDescopeSdk from '@descope/web-js-sdk';
import Cookies from 'js-cookie';
import store from 'store';

import {
  extractAccessTokenFromDescopeCookie,
  extractIDTokenFromDescopeCookie,
  extractRefreshTokenFromDescopeCookie
} from './functions';
import { clearShownTableSlots } from './tableCapacity';
import { APP_DOMAIN, DESCOPE_COOKIE } from './urls';

// --- Refresh-scheduler state -------------------------------------------------
// Two shared module-level pieces of state so a mid-hand access-token expiry can
// no longer silently boot the player back to the guest lobby:
//
//   REFRESH_MARGIN_MS — grace window `hasValidAuthToken` honors AFTER exp, so a
//     token that has just crossed exp is still considered valid long enough for
//     an in-flight refresh to swap in the new access_token.
//
//   refreshInFlight — flipped true while `refreshAccessToken` awaits Descope's
//     /v1/auth/refresh response. During that window `hasValidAuthToken` returns
//     true unconditionally, so the session-end effect in App.tsx cannot race
//     ahead and tear the SPA down mid-refresh.
//
// See docs/investigations/20260821-mid-game-player-logout-no-refresh-scheduler.md
// for the full RCA (dominant mechanism: React re-renders during a hand →
// useGuest → hasValidAuthToken → the exp check flipped true the instant wall
// clock crossed exp, with no scheduled refresh to save it).
export const REFRESH_MARGIN_MS = 60_000;
// Hard floor between refresh attempts. Regardless of what the token exp says,
// we never fire refresh more often than this. Defense against a runaway loop
// where a mis-configured tenant returns sessions with exp < REFRESH_MARGIN_MS
// (my scheduler would compute delay=0 immediately on every response, fire the
// next refresh right away, and hammer Descope until the tenant rate-limits
// with a CORS-blocked 429). Field-observed in a QA env: 58 refreshes in ~5
// minutes before the browser started refusing.
export const MIN_REFRESH_INTERVAL_MS = 30_000;
// Hard timeout on the Descope SDK's /v1/auth/refresh HTTP call. A hung TLS
// handshake / captive portal / silently-dropped network would otherwise
// leave `refreshInFlight = true` forever, and `hasValidAuthToken` would
// permanently report "member" against a token that will never be renewed
// — silently unrecoverable until page reload. 30s is long enough for any
// realistic transient slowness on a mobile network.
export const REFRESH_CALL_TIMEOUT_MS = 30_000;

// Descope error codes signaling the session is TERMINALLY dead server-side —
// no client-side retry will recover it, the family is invalidated. Detecting
// these lets us stop hammering Descope with dead tokens and hand off to the
// session-end effect immediately so the user re-logs in.
const TERMINAL_REFRESH_ERROR_CODES = new Set(['E064006', 'E064003']);
let refreshInFlight = false;
// Deduplicate concurrent refresh calls — Descope tenants with refresh-token
// rotation invalidate the ENTIRE token family on reuse detection, so a second
// refresh firing while the first is still awaiting reuses R1 and permanently
// nukes the session (error E064006 "JWT family ID invalidated"). This holds
// the single in-flight promise so every concurrent caller awaits the SAME
// network round-trip.
let refreshInFlightPromise: Promise<boolean> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
// Ownership token for the currently-running fireRefreshOnce invocation.
// When ANY caller reschedules (startTokenRefresh, cancelRefreshTimer, or
// another fireRefreshOnce), this rotates — the running one bails cleanly
// on wake-up instead of overwriting the newer timer handle (which would
// orphan it and defeat stopTokenRefresh).
let currentTimerOwner: symbol | null = null;
let refreshAttempt = 0;
// Wall-clock time (ms) of the last completed refresh. Enforces the
// MIN_REFRESH_INTERVAL_MS floor.
let lastRefreshAt = 0;
// Set true when refreshAccessToken hit a TERMINAL_REFRESH_ERROR_CODES code
// on THIS attempt. fireRefreshOnce reads this to stop the backoff chain —
// nothing on the client can recover a Descope family invalidation; the
// session must be torn down and the user re-logs in. Reset per-attempt.
let lastRefreshErrorTerminal = false;
// Persistent for the current session: once Descope tells us the family is
// invalidated, EVERY subsequent refresh attempt against the same session
// will fail the same way. The watchdog would otherwise keep re-arming
// attempts every 30s until exp+margin elapsed (~120 Descope calls/hour of
// pointless E064003 spam). This flag survives across `startTokenRefresh`
// calls and is only cleared by `clearAuth`/`stopTokenRefresh`/successful
// `storeAuthTokens` — i.e., a genuinely new session.
let sessionTerminallyDead = false;

// --- Auth-change subscription ------------------------------------------------
// isGuestSession() reads storage synchronously and is not reactive on its own.
// storeAuthTokens()/clearAuthState() notify these listeners so React consumers
// (useGuest via useSyncExternalStore) re-render when the session changes —
// enabling the guest→member switch without a full page reload.
const authListeners = new Set<() => void>();

export function subscribeAuthChange(cb: () => void): () => void {
  authListeners.add(cb);
  return () => {
    authListeners.delete(cb);
  };
}

export function notifyAuthChange(): void {
  guestSnapshot = null; // session changed - next isGuestSession() re-derives
  authListeners.forEach((cb) => cb());
}

// --- Guest-state cache ---------------------------------------------------
// isGuestSession() is useGuest's useSyncExternalStore snapshot, so it runs on
// EVERY RENDER of EVERY consumer - and uncached it costs a document.cookie
// read (a synchronous IPC in the Android WebView), a localStorage read, a
// JSON.parse and a JWT base64 decode, per call. Profiled at 224ms of pure
// cookie IPC across one fast table-swipe loop on device (logged-in sessions
// pay the whole pipeline; guests short-circuit at the missing cookie - why
// the lag "appeared" after logging in). The session only actually changes
// through storeAuthTokens/clearAuthState, which invalidate this via
// notifyAuthChange; the short TTL bounds staleness against anything writing
// the cookie behind our back (the Descope SDK's own refresh), and matters
// only for that edge - the notify path clears it instantly.
const GUEST_SNAPSHOT_TTL_MS = 5_000;
let guestSnapshot: { value: boolean; at: number } | null = null;

/**
 * Get the raw Descope cookie/token string, with localStorage fallback for iOS.
 */
export function getDescopeCookieRaw(): string {
  let cookie = Cookies.get(DESCOPE_COOKIE as string);
  if (!cookie || typeof cookie !== 'string') {
    try {
      const stored = localStorage.getItem(DESCOPE_COOKIE as string) || '';
      if (stored.startsWith('{') || stored.startsWith('%7B')) {
        cookie = stored;
      }
    } catch {
      /* noop */
    }
  }
  return cookie || '';
}

/**
 * Get the access token JWT string, with iOS localStorage fallback.
 */
export function getAccessToken(): string {
  return extractAccessTokenFromDescopeCookie(getDescopeCookieRaw());
}

/**
 * Get the Descope ID token JWT string. Used by backoffice endpoints that
 * Unity authenticates with the ID token (e.g. /api/players/today/deposit).
 */
export function getIdToken(): string {
  return extractIDTokenFromDescopeCookie(getDescopeCookieRaw());
}

/**
 * Read fresh auth tokens from cookies/localStorage.
 * Called on every connect/reconnect — never cached.
 */
export function getAuthToken(): { access_token: string | null; refresh_token: string | null } {
  let descopeCookie = Cookies.get(DESCOPE_COOKIE as string);
  if (!descopeCookie || typeof descopeCookie !== 'string') {
    try {
      const stored = localStorage.getItem(DESCOPE_COOKIE as string) || '';
      if (stored.startsWith('{') || stored.startsWith('%7B')) {
        descopeCookie = stored;
      }
    } catch {
      /* noop */
    }
  }
  if (!descopeCookie) {
    return { access_token: null, refresh_token: null };
  }
  return {
    access_token: extractAccessTokenFromDescopeCookie(descopeCookie),
    refresh_token: extractRefreshTokenFromDescopeCookie(descopeCookie)
  };
}

/**
 * Decode a JWT payload segment. JWTs are base64url-encoded (RFC 7515): they use
 * '-'/'_' instead of '+'/'/' and drop '=' padding. The browser's atob() only
 * accepts standard base64 and throws on '-'/'_', so we must convert first —
 * otherwise every Descope session token fails to decode and the auth gate
 * treats a perfectly valid cookie as invalid.
 */
export function decodeJwtPayload(token: string): any {
  const segment = token.split('.')[1];
  if (!segment) throw new Error('Invalid JWT: missing payload segment');
  let base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  return JSON.parse(atob(base64));
}

/**
 * Check if user has a valid (non-expired) auth token.
 *
 * The exp check honors `REFRESH_MARGIN_MS` — a token that has just crossed
 * exp is still counted as valid inside that grace window so the async
 * refresh round-trip completes before the session-end effect tears the SPA
 * down mid-hand. When a refresh is actively in-flight, the token is treated
 * as valid regardless: startTokenRefresh has already committed to renewing
 * it, and there is nothing useful to gain from flipping the app to guest
 * mid-refresh.
 */
export function hasValidAuthToken(): boolean {
  if (refreshInFlight) return true;

  let descopeCookie = Cookies.get(DESCOPE_COOKIE as string);
  const lsToken = localStorage.getItem(DESCOPE_COOKIE as string) || '';

  if (!descopeCookie || typeof descopeCookie !== 'string') {
    try {
      if (lsToken.startsWith('{') || lsToken.startsWith('%7B')) {
        descopeCookie = lsToken;
      }
    } catch {
      /* noop */
    }
  }
  if (!descopeCookie) return false;

  try {
    const parsed = JSON.parse(descopeCookie);
    const token = parsed?.access_token || parsed?.id_token;
    if (!token) return false;

    const payload = decodeJwtPayload(token);
    if (payload.exp && payload.exp * 1000 < Date.now() - REFRESH_MARGIN_MS) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Store auth tokens in both cookie and localStorage.
 *
 * Every stored session is also the anchor for its own next refresh — login,
 * refresh success, and the web-login handoff all converge here, so scheduling
 * the next refresh from inside this call keeps the timer chain unbroken
 * across every write path.
 */
export function storeAuthTokens(sessionJwt: string, refreshJwt: string) {
  // A successful token store IS the "new session" signal that resets the
  // terminally-dead flag. Login handoff, client-side refresh success, and
  // engine-side gotRefreshToken all funnel through here — anything that
  // produces a fresh valid token means the previous terminal state (if any)
  // was for a session that no longer applies.
  sessionTerminallyDead = false;
  const tokenData = {
    access_token: sessionJwt,
    refresh_token: refreshJwt || '',
    id_token: sessionJwt
  };
  const tokenStr = JSON.stringify(tokenData);

  if (Capacitor.isNativePlatform()) {
    Cookies.set(DESCOPE_COOKIE as string, tokenStr, { path: '/' });
    try {
      localStorage.setItem(DESCOPE_COOKIE as string, tokenStr);
    } catch {
      /* noop */
    }
  } else {
    // Write BOTH host-only and domain-scoped variants — App.tsx's
    // GOT_REFRESH_TOKEN handler and clearAuth both operate on both
    // variants, so the domain-scoped copy CAN exist in the wild. If we
    // only wrote the host-only one here, `Cookies.get` (which returns
    // the first match in document.cookie order) could shadow a fresh
    // refresh with a stale domain-scoped copy — reproducing the original
    // mid-game logout while `[Auth] refresh success` logs cleanly.
    // Also mirror to localStorage as a same-origin fallback for iOS
    // WKWebView (where cookies can be quirky) — matches the native path.
    Cookies.set(DESCOPE_COOKIE as string, tokenStr, {
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'lax'
    });
    Cookies.set(DESCOPE_COOKIE as string, tokenStr, {
      path: '/',
      domain: `.${APP_DOMAIN}`,
      secure: window.location.protocol === 'https:',
      sameSite: 'lax'
    });
    try {
      localStorage.setItem(DESCOPE_COOKIE as string, tokenStr);
    } catch {
      /* noop */
    }
  }

  // Reschedule the next refresh against the new access_token's exp.
  startTokenRefresh();

  // Session changed (guest → member) — wake reactive consumers (useGuest).
  notifyAuthChange();
}

/**
 * Build a Descope SDK instance for a projectId. Its `refresh` method wraps
 * the exact request shape Descope expects (auth header format, empty body,
 * credentials, project-id header) — hand-rolling the fetch produced 401s in
 * field testing because a subtle detail was wrong. Delegating to the SDK
 * eliminates that entire class of bug.
 *
 * `persistTokens` and `autoRefresh` are both false
 * because THIS module owns the storage (via storeAuthTokens) and scheduling
 * (startTokenRefresh) — we only want the SDK's HTTP call, not its
 * cookie/interval machinery. SDK creation is a cheap factory call — no
 * network, no side effects with those flags off — so we don't bother
 * caching across calls (the resulting module-level state complicates test
 * isolation for no real production win).
 */
// Injectable SDK factory — production wraps @descope/web-js-sdk, tests can
// override via __setDescopeSdkFactoryForTests. Keeps the module free of the
// hoisting / require-timing pitfalls that made jest.mock('@descope/web-js-sdk')
// unreliable on this test suite.
type DescopeSdkLike = { refresh: (token?: string) => Promise<any> };
type DescopeSdkFactory = (projectId: string) => DescopeSdkLike;

let sdkFactory: DescopeSdkFactory = (projectId) =>
  createDescopeSdk({
    projectId,
    persistTokens: false,
    autoRefresh: false
  });

/** Test-only override. Never call from production code. */
export function __setDescopeSdkFactoryForTests(factory: DescopeSdkFactory): void {
  sdkFactory = factory;
}

function getDescopeSdk(projectId: string): DescopeSdkLike {
  return sdkFactory(projectId);
}

/**
 * Refresh the access token via Descope's SDK. On success stores the fresh
 * access_token / refresh_token via storeAuthTokens() (which also reschedules
 * the next refresh). On failure returns false — startTokenRefresh's caller
 * decides whether to retry or let the token genuinely expire.
 *
 * The projectId is extracted from the refresh JWT's `iss` claim, which every
 * Descope-issued JWT carries as either the bare projectId or
 * `https://api.descope.com/<projectId>`.
 *
 * refreshInFlight is set for the duration so hasValidAuthToken returns true
 * unconditionally while the refresh network round-trip is pending — closing
 * the race window where a still-valid but almost-expired token could
 * otherwise trip the session-end effect on a mid-refresh render.
 */
export async function refreshAccessToken(): Promise<boolean> {
  // Deduplicate — if a refresh is already awaiting Descope, every concurrent
  // caller awaits the SAME promise. A second sdk.refresh() with the same
  // refresh_token triggers Descope's rotation-reuse detection (E064006
  // "JWT family ID invalidated") and PERMANENTLY kills the session, so this
  // guard is load-bearing, not a nicety.
  if (refreshInFlightPromise) return refreshInFlightPromise;
  refreshInFlightPromise = doRefreshAccessToken();
  try {
    return await refreshInFlightPromise;
  } finally {
    refreshInFlightPromise = null;
  }
}

async function doRefreshAccessToken(): Promise<boolean> {
  const { refresh_token } = getAuthToken();
  if (!refresh_token) return false;

  let projectId = '';
  try {
    const payload = decodeJwtPayload(refresh_token);
    const iss = String(payload?.iss || '');
    // Descope `iss` is either the bare projectId or a URL ending in it.
    projectId = iss.includes('/') ? iss.split('/').filter(Boolean).pop() || '' : iss;
    if (!projectId) return false;
  } catch {
    return false;
  }

  refreshInFlight = true;
  // Stamp BEFORE sdk.refresh — storeAuthTokens (called inside the success
  // branch) reaches startTokenRefresh synchronously, and startTokenRefresh
  // must see a non-zero lastRefreshAt to enforce the floor. Stamping in a
  // finally block after storeAuthTokens has already rescheduled is too late.
  lastRefreshAt = Date.now();
  try {
    const sdk = getDescopeSdk(projectId);
    // Timeout the SDK call so a hung TLS / captive-portal / dropped
    // network can't leave `refreshInFlight` stuck true forever — which
    // would make hasValidAuthToken permanently report "member" against a
    // dead token, silently unrecoverable until page reload.
    const resp = await Promise.race([
      sdk.refresh(refresh_token),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('refresh_timeout')), REFRESH_CALL_TIMEOUT_MS)
      )
    ]);
    if (!resp?.ok || !resp?.data) {
      const errorCode = resp?.error?.errorCode || '';
      console.warn(
        '[Auth] refresh failed — status',
        errorCode || resp?.error?.errorDescription || resp?.code || 'unknown'
      );
      // Terminal error — the family is dead server-side, no retry will
      // recover. Mark for the caller to short-circuit and stop hammering
      // Descope with tokens Descope has already invalidated. Set BOTH the
      // per-attempt flag (fireRefreshOnce reads it) AND the persistent
      // session flag (startTokenRefresh refuses to schedule anymore) so
      // the watchdog can't resurrect the retry loop 30s later.
      if (TERMINAL_REFRESH_ERROR_CODES.has(errorCode)) {
        lastRefreshErrorTerminal = true;
        sessionTerminallyDead = true;
        // Wake the ONE teardown owner deterministically. `verifyRefreshNow()`
        // is fire-and-forget by design, so its callers (waitlistService's
        // handleAuthFailure, App's GOT_REFRESH_TOKEN handler, both of
        // ConnectionManager's auth handlers) never learn the family was
        // repudiated. Without this notify, nothing invalidates the
        // GUEST_SNAPSHOT_TTL_MS cache and nothing re-renders useGuest, so the
        // flip to guest — and therefore App's isSessionEndTransition teardown —
        // waited on whatever unrelated render happened to re-read the snapshot
        // next. That is a completion path with no owner.
        //
        // Deliberately just a notify, NOT a clearAuth()/hardReload() here:
        // teardown stays with the single existing owner that already carries
        // the diagnostics, and this correctly stays a no-op while the
        // access_token is still inside REFRESH_MARGIN_MS — an E064003 against a
        // session token that is still usable must not kill it early.
        notifyAuthChange();
      }
      return false;
    }
    // Descope's SDK returns { sessionJwt, refreshJwt } in resp.data.
    const sessionJwt = resp.data.sessionJwt;
    const refreshJwt = resp.data.refreshJwt || refresh_token;
    if (!sessionJwt) {
      console.warn('[Auth] refresh response missing sessionJwt');
      return false;
    }
    storeAuthTokens(sessionJwt, refreshJwt);
    console.log('[Auth] refresh success');
    return true;
  } catch (err) {
    console.warn('[Auth] refresh threw', err);
    return false;
  } finally {
    refreshInFlight = false;
  }
}

/**
 * Schedule an access-token refresh to fire REFRESH_MARGIN_MS before exp.
 * Idempotent — safe to call from mount effects, from storeAuthTokens(), and
 * as a listener on subscribeAuthChange. No-op when no session is present.
 *
 * Fires against `min(access_token.exp, refresh_token.exp)` — a tenant with
 * refresh_TTL < session_TTL (unusual, but seen in QA stress-tests) would
 * otherwise let the refresh_token die before the timer fires, and the
 * subsequent refresh call sends an expired refresh → E064003.
 *
 * On refresh failure the retry backoff is floored to MIN_REFRESH_INTERVAL_MS
 * so a burst of transient errors can't hammer Descope. If the token stays
 * past exp + REFRESH_MARGIN_MS without a successful refresh, hasValidAuthToken
 * flips false and the App.tsx session-end effect runs — which is the honest
 * outcome for a session Descope no longer trusts.
 */
export function startTokenRefresh(): void {
  // Refuse to schedule against a session Descope has already invalidated.
  // Without this the watchdog's setInterval(startTokenRefresh, 30s) would
  // re-arm attempts every 30s after a terminal error — exactly the
  // runaway-refresh pattern MIN_REFRESH_INTERVAL_MS was meant to prevent.
  // Cleared only by a genuinely new session (storeAuthTokens after a fresh
  // login) or an explicit clearAuth/stopTokenRefresh teardown.
  if (sessionTerminallyDead) return;

  // Cancel any pending timer without touching refreshAttempt — the attempt
  // counter drives backoff across an in-progress retry chain and must survive
  // an outside caller rescheduling on top of us.
  cancelRefreshTimer();

  // Only the refresh_token is load-bearing here: it is the one credential
  // `doRefreshAccessToken()` actually spends (it reads `iss` from it and sends
  // it to Descope). Requiring an access_token too meant a stored blob holding
  // only a live refresh token — which `canAttemptRefresh()` accepts, so the
  // bootstrap path routes it to 'refresh' — could never arm a retry after a
  // transient failure.
  const { access_token, refresh_token } = getAuthToken();
  if (!refresh_token) return;

  const readExp = (jwt: string): number | null => {
    try {
      const payload = decodeJwtPayload(jwt);
      return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  };

  const now = Date.now();
  const accessExpMs = access_token ? readExp(access_token) : null;
  const refreshExpMs = readExp(refresh_token);

  // Whichever expires first is our deadline — a refresh_token that dies
  // before the access_token wins the race (unusual tenant config, but the
  // scheduler MUST handle it or refresh calls fire against a dead token).
  //
  // Collected as a list rather than requiring BOTH exps, which is what used to
  // contradict `canAttemptRefresh()`: that predicate treats an `exp`-less
  // refresh token as refreshable on purpose (Descope is the authority on a
  // token it minted without an expiry), but this function then bailed on it and
  // armed nothing. The bootstrap retry branch calls us after a transient
  // failure, so bailing left the session holding a spendable refresh token with
  // nothing scheduled to spend it — and no other rescue, because both watchdogs
  // in App.tsx open with `if (isGuest) return;` while the bootstrap path stays
  // in guest state across the round trip. Nothing recovered until a remount.
  //
  // The two null cases are NOT the same and must not be collapsed:
  //  - token PRESENT but carrying no `exp` → contributes no deadline; defer to
  //    whatever the other token says.
  //  - token ABSENT entirely → a deadline of `now`. There is no session left to
  //    protect and everything to gain by refreshing immediately; anchoring to
  //    the refresh token's own exp instead would schedule the retry ~30 days
  //    out, which is indistinguishable from not scheduling at all.
  const deadlines: number[] = [];
  if (!access_token) deadlines.push(now);
  else if (accessExpMs !== null) deadlines.push(accessExpMs);
  if (refreshExpMs !== null) deadlines.push(refreshExpMs);

  // Neither token yields a deadline (both present, neither carries an exp).
  // Don't invent a bespoke backoff policy — treat it as due now and let the
  // MIN_REFRESH_INTERVAL_MS floor below apply the same cadence every other
  // repeated attempt already honors.
  const effectiveExpMs = deadlines.length > 0 ? Math.min(...deadlines) : now;

  // Honor MIN_REFRESH_INTERVAL_MS since the last completed refresh — closes
  // the runaway-loop trap where a token with exp inside REFRESH_MARGIN_MS
  // would fire back-to-back refresh calls until Descope rate-limits.
  const sinceLast = now - lastRefreshAt;
  const floor = lastRefreshAt === 0 ? 0 : Math.max(0, MIN_REFRESH_INTERVAL_MS - sinceLast);
  const rawDelay = Math.max(0, effectiveExpMs - now - REFRESH_MARGIN_MS);
  const delay = Math.max(floor, rawDelay);
  refreshTimer = setTimeout(fireRefreshOnce, delay);
}

/**
 * Force an immediate refresh attempt, bypassing the exp-based schedule.
 * Callers use this when they've received an out-of-band signal that the
 * current session is suspect and want to VERIFY with Descope right now
 * — e.g. an engine `gotRefreshToken invalid_grant` on a token whose exp
 * is still future, or a REST 401 while `hasValidAuthToken` returns true.
 *
 * If Descope confirms the session is dead (terminal error), the terminal
 * short-circuit + `sessionTerminallyDead` flag stop the watchdog from
 * resurrecting attempts, and `hasValidAuthToken` will drift past the
 * margin naturally so the session-end effect fires. If the token is
 * actually still good (transient upstream hiccup), we get a fresh session
 * and continue as normal. Either way, we DON'T wait 9 minutes for the
 * next scheduled refresh to find out.
 *
 * Still honored: dedup (concurrent callers share the in-flight promise)
 * and the terminally-dead flag (won't spam Descope after the first
 * terminal signal).
 */
export function verifyRefreshNow(): void {
  if (sessionTerminallyDead) return;
  // Fire and forget — the promise result is handled inside refreshAccessToken
  // via storeAuthTokens (success) or the terminal-error flag (failure).
  refreshAccessToken().catch(() => {
    /* refreshAccessToken swallows its own errors and returns false */
  });
}

/**
 * Read-only accessor for the terminally-dead session flag — Descope has
 * repudiated this token family (E064006 / E064003) and no client-side retry
 * can recover it.
 *
 * Needed by the cold-start bootstrap path, which must distinguish "the
 * refresh failed, try again in a moment" from "the refresh failed and always
 * will, tear the session down" WITHOUT re-entering `refreshAccessToken` just
 * to find out.
 */
export function isSessionTerminallyDead(): boolean {
  return sessionTerminallyDead;
}

/**
 * Whether an expired session token is RECOVERABLE — i.e. a refresh token is
 * present, still within its own `exp`, and Descope hasn't already repudiated
 * the family.
 *
 * This is the predicate the whole cold-start bug turned on. `hasValidAuthToken()`
 * answers "may I use this session right now"; it says nothing about whether the
 * session can be renewed. Every teardown site used to read only the former, so
 * the ONE case a refresh exists for — session token expired, refresh token still
 * good — was indistinguishable from "no session at all" and got the same
 * `clearAuth()`. On a tenant with a 1-day session token and a 30-day refresh
 * token that meant a forced login every single day, because `clearAuth()`
 * deletes the refresh token before anything ever tries to spend it.
 *
 * Deliberately synchronous and side-effect free: it is called from a React
 * render path (`App.tsx`'s bootstrap `useState` initializer) and from the 401
 * handlers, so it must never itself kick a network call.
 *
 * A refresh token with no `exp` claim counts as refreshable — mirroring
 * `hasValidAuthToken`'s treatment of a session token with no `exp`. Let
 * Descope be the authority on a token it minted without an expiry rather than
 * guessing client-side.
 */
export function canAttemptRefresh(): boolean {
  if (sessionTerminallyDead) return false;

  const { refresh_token } = getAuthToken();
  if (!refresh_token) return false;

  try {
    const payload = decodeJwtPayload(refresh_token);
    if (typeof payload?.exp !== 'number') return true;
    return payload.exp * 1000 > Date.now();
  } catch {
    // Undecodable refresh token — `doRefreshAccessToken` would bail on it too
    // (it reads `iss` from this same payload), so calling it would be a
    // guaranteed-failed round trip.
    return false;
  }
}

/**
 * Refresh once. On success, `storeAuthTokens` has already rescheduled us
 * against the new token — nothing more to do. On failure, chain the next
 * backoff attempt directly (no round-trip through startTokenRefresh, which
 * would re-read the still-expired token and short-circuit the retry chain).
 */
async function fireRefreshOnce(): Promise<void> {
  // Take ownership of the timer slot at the very top. If anyone else
  // reschedules during our await, they'll take the slot from us; when we
  // come back, we compare and only touch the slot if it's still ours —
  // never overwrite a fresher timer someone else scheduled. This is the
  // fix for the orphan-timer bug: without ownership tracking, a watchdog
  // tick during our await would set refreshTimer to a new handle, our
  // post-await reassignment would overwrite the module var (leaving the
  // watchdog's timer running but uncancellable), and stopTokenRefresh
  // could never fully clean up.
  const myOwnershipToken = Symbol('fireRefreshOnce');
  currentTimerOwner = myOwnershipToken;
  refreshTimer = null;
  lastRefreshErrorTerminal = false;

  const ok = await refreshAccessToken();

  // If a fresher scheduler (watchdog, storeAuthTokens, etc.) armed a new
  // timer during our await, they own it now — bail cleanly.
  if (currentTimerOwner !== myOwnershipToken) return;

  if (ok) {
    refreshAttempt = 0;
    return;
  }
  // Terminal error (Descope family invalidated / refresh JWT genuinely
  // invalid) — no retry will help. Stop the backoff chain, let
  // hasValidAuthToken drift past the margin naturally, and the session-end
  // effect will fire clearAuth + hardReload with the diagnostic log so the
  // user re-logs in. Hammering Descope with dead tokens just spams their
  // rate-limits (and produced 6+ back-to-back 401s in QA).
  if (lastRefreshErrorTerminal) {
    console.warn('[Auth] refresh terminally failed — session dead, no retry');
    refreshAttempt = 0;
    return;
  }
  refreshAttempt++;
  // Floor retry cadence at MIN_REFRESH_INTERVAL_MS so even repeated non-
  // terminal failures can't hammer Descope tighter than one call per 30s.
  const rawBackoff = Math.min(60_000, 5_000 * 2 ** (refreshAttempt - 1));
  const backoff = Math.max(MIN_REFRESH_INTERVAL_MS, rawBackoff);
  refreshTimer = setTimeout(fireRefreshOnce, backoff);
}

/** Internal — clear the pending timer without resetting attempt state.
 * Also rotates currentTimerOwner so any in-flight fireRefreshOnce comes
 * back from its await and sees it no longer owns the slot — bails cleanly
 * instead of arming a new backoff timer that stopTokenRefresh couldn't
 * see.
 */
function cancelRefreshTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  currentTimerOwner = null;
}

/**
 * Cancel any pending refresh timer and reset the backoff counter. Called
 * from clearAuth() and from any caller that wants a hard stop (tests,
 * teardown). Distinct from cancelRefreshTimer() because a mid-backoff
 * self-reschedule must NOT lose its attempt counter.
 */
export function stopTokenRefresh(): void {
  cancelRefreshTimer();
  refreshAttempt = 0;
  lastRefreshAt = 0;
  sessionTerminallyDead = false;
}

/**
 * Read the stored access token's `exp` claim as an ms timestamp, or null if
 * no token exists or its payload can't be decoded. Callers use this to log
 * how far past exp we were at the moment a session-end effect fired — the
 * one datum that cleanly distinguishes "the token really expired" from
 * "something else flipped useGuest to true".
 */
export function readAccessTokenExp(): number | null {
  const { access_token } = getAuthToken();
  if (!access_token) return null;
  try {
    const payload = decodeJwtPayload(access_token);
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Adopt a one-time session handoff carried in the URL fragment by the auth
 * app's web /loginapp flow (apps/auth LoginApp.js / loginRedirectHelper.js).
 *
 * Needed because the auth app is reachable from two real hostnames with two
 * different apex domains (auth.hijack.poker and auth.hijackpoker.com in
 * production), while this game app only ever serves from hijackpoker.com — a
 * cookie written by auth.hijack.poker can never be read here (browsers only
 * allow a page to set a cookie's domain to its own host or a parent of it),
 * so a same-apex cookie only works for logins that happened to start on
 * auth.hijackpoker.com. The auth app instead appends the session/refresh JWTs
 * as a URL fragment (never sent to any server, unlike a query string) on
 * every /loginapp redirect back here regardless of which hostname it came
 * from; this reads them once, stores them via the normal storeAuthTokens()
 * path (same-origin, so it always works), and strips the fragment from the
 * URL so it isn't left sitting in browser history.
 *
 * Call this once, before the app renders (see index.tsx) — storeAuthTokens'
 * notifyAuthChange() only wakes components that are already mounted and
 * subscribed, so adopting the token any later could race the first render.
 */
export function adoptWebLoginHandoff(): void {
  if (typeof window === 'undefined' || Capacitor.isNativePlatform()) return;
  try {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash.includes('session_jwt=')) return;

    const params = new URLSearchParams(hash);
    const sessionJwt = params.get('session_jwt');
    const refreshJwt = params.get('refresh_jwt');
    if (!sessionJwt) return;

    storeAuthTokens(sessionJwt, refreshJwt || '');

    params.delete('session_jwt');
    params.delete('refresh_jwt');
    const rest = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + (rest ? `#${rest}` : '')
    );
  } catch {
    /* noop */
  }
}

/**
 * A "guest" is simply any visitor without a valid session. Not-logged-in users
 * land on the guest lobby instead of being forced to the login screen, so guest
 * mode is derived purely from auth state (no opt-in flag). Logged-in users are
 * never guests. Used by the socket layer (guest handshake), the App router
 * (guest vs member layout), and the lobby CTAs.
 */
export function isGuestSession(): boolean {
  const now = Date.now();
  if (guestSnapshot && now - guestSnapshot.at < GUEST_SNAPSHOT_TTL_MS) {
    return guestSnapshot.value;
  }
  const value = !hasValidAuthToken();
  guestSnapshot = { value, at: now };
  return value;
}

/**
 * Send a guest to the sign-up page. Used by lobby CTAs ("Sign up to play")
 * and any play action a guest attempts. Mirrors the web-only redirect guard
 * used elsewhere (no-op redirect inside a native shell).
 */
export function redirectToSignup(): void {
  if (!Capacitor.isNativePlatform()) {
    window.location.replace(`https://account.${APP_DOMAIN}/signup`);
  }
}

// --- Post-login return path --------------------------------------------------
// Whatever page a guest was on when they chose to log in (a table, the grid, a
// tournament) is stashed here so the login success handler — web's LoginScreen
// onSuccess and native's useNativeLoginDeepLink — can send them back to it
// instead of always landing on the lobby. sessionStorage survives both the
// web same-tab redirect to /login and native's round trip through the system
// browser (the Capacitor webview keeps running underneath it).
const LOGIN_RETURN_PATH_KEY = 'postLoginReturnPath';
// ViewManagerContext treats a fresh mount on a table URL as a page refresh and
// bounces it to the lobby (Unity parity). Our post-login hard-reload lands on
// a table URL too and looks identical to that refresh, so it gets bounced
// right back — this flag tells that guard "this mount is the post-login
// redirect, not a refresh, let it through" for exactly one mount.
const POST_LOGIN_LANDING_KEY = 'postLoginLanding';

function captureLoginReturnPath(): void {
  if (typeof window === 'undefined') return;
  try {
    // observe=yes is how a GUEST views a table (ViewManagerContext.openTable /
    // SaloonLobbyContainer.onOpenTable only ever add it for guest sessions).
    // Whoever consumes this stash has just authenticated, so it's never a
    // guest by the time they land back here — carrying the param forward
    // drops a fresh member into observer mode, which makes BuyInModal's
    // isPlayerObserver gate block the auto-join's buy-in dialog from ever
    // rendering, even though openAskBuyin was correctly set.
    const params = new URLSearchParams(window.location.search);
    params.delete('observe');
    const search = params.toString();
    const path = `${window.location.pathname}${search ? `?${search}` : ''}`;
    if (path && path !== '/login') {
      sessionStorage.setItem(LOGIN_RETURN_PATH_KEY, path);
    }
  } catch {
    /* noop */
  }
}

/**
 * Read and clear the stashed pre-login path. Falls back to the lobby ('/')
 * when nothing was stashed (e.g. login initiated some other way).
 */
export function consumeLoginReturnPath(): string {
  if (typeof window === 'undefined') return '/';
  try {
    const path = sessionStorage.getItem(LOGIN_RETURN_PATH_KEY);
    sessionStorage.removeItem(LOGIN_RETURN_PATH_KEY);
    if (path) {
      sessionStorage.setItem(POST_LOGIN_LANDING_KEY, '1');
    }
    return path || '/';
  } catch {
    return '/';
  }
}

/**
 * Read and clear the post-login-landing flag. True only for the single mount
 * immediately following a login redirect back to a non-lobby page.
 */
export function consumePostLoginLanding(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const flag = sessionStorage.getItem(POST_LOGIN_LANDING_KEY) === '1';
    sessionStorage.removeItem(POST_LOGIN_LANDING_KEY);
    return flag;
  } catch {
    return false;
  }
}

// A guest's "Login/Sign up to Join" click doesn't just want the login page —
// it wants the seat. This records which table they meant to join so that once
// they're back and authenticated, JoinButton can finish the job itself
// instead of making them find the same table and click Join again. Scoped to
// the gameID (not a bare flag) so it can only ever fire for the table it was
// recorded for — landing anywhere else (or on a different table) just drops it.
const JOIN_INTENT_KEY = 'postLoginJoinIntentGameID';

export function markJoinIntentBeforeLogin(gameID: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(JOIN_INTENT_KEY, gameID);
  } catch {
    /* noop */
  }
}

/**
 * True (and clears the stash) only for the exact table it was recorded for.
 * On a fresh post-login mount, JoinButton's gameID (from Redux) reads empty
 * until the socket delivers table data — a non-matching call must NOT clear
 * the stash, or the real match a moment later (once gameID resolves) would
 * find nothing left to consume.
 */
export function consumeJoinIntent(gameID: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stashedGameID = sessionStorage.getItem(JOIN_INTENT_KEY);
    if (!stashedGameID) return false;
    const matches = stashedGameID === gameID;
    if (matches) {
      sessionStorage.removeItem(JOIN_INTENT_KEY);
    }
    return matches;
  } catch {
    return false;
  }
}

/**
 * Same match check as consumeJoinIntent, but never clears the stash. Lets a
 * caller debounce its decision — keep rescheduling while gameID/identity/table
 * data are still settling — without losing the intent to a premature clear.
 * The caller is responsible for eventually calling consumeJoinIntent once it
 * actually commits to an action.
 */
export function peekJoinIntent(gameID: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(JOIN_INTENT_KEY) === gameID;
  } catch {
    return false;
  }
}

// Clicking a specific open seat (rather than the JoinButton CTA) carries the
// same "I want to sit here" intent, but needs the seatId too — OpenSeat also
// calls setManuallyAssignedSeatId(seatId), which JoinButton never does.
// Multiple OpenSeat instances mount at once (one per open seat on the table),
// so unlike consumeJoinIntent this must only clear the stash when IT matches
// — otherwise whichever seat's effect happens to run first would consume (and
// discard) an intent meant for a different seat.
const SEAT_INTENT_KEY = 'postLoginSeatIntent';

export function markSeatIntentBeforeLogin(gameID: string, seatId: number): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(SEAT_INTENT_KEY, `${gameID}:${seatId}`);
  } catch {
    /* noop */
  }
}

/**
 * True (and clears the stash) only for the exact table + seat it was recorded
 * for. Any other seat's call leaves the stash untouched so the right seat can
 * still find it.
 */
export function consumeSeatIntent(gameID: string, seatId: number): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stashed = sessionStorage.getItem(SEAT_INTENT_KEY);
    if (!stashed) return false;
    const [stashedGameID, stashedSeatId] = stashed.split(':');
    const matches = stashedGameID === gameID && Number(stashedSeatId) === seatId;
    if (matches) {
      sessionStorage.removeItem(SEAT_INTENT_KEY);
    }
    return matches;
  } catch {
    return false;
  }
}

/**
 * Same match check as consumeSeatIntent, but never clears the stash. Lets a
 * caller debounce its decision — keep rescheduling while gameID/identity/seat
 * data are still settling — without losing the intent to a premature clear.
 */
export function peekSeatIntent(gameID: string, seatId: number): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stashed = sessionStorage.getItem(SEAT_INTENT_KEY);
    if (!stashed) return false;
    const [stashedGameID, stashedSeatId] = stashed.split(':');
    return stashedGameID === gameID && Number(stashedSeatId) === seatId;
  } catch {
    return false;
  }
}

// The "Login to contact support" gate (lobby header / table header) — not
// table-scoped, just a plain one-shot flag. Only one of the two header
// components is ever mounted for a given page, so there's no cross-consumer
// race to guard against here.
const SUPPORT_INTENT_KEY = 'postLoginSupportIntent';

export function markSupportIntentBeforeLogin(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(SUPPORT_INTENT_KEY, '1');
  } catch {
    /* noop */
  }
}

export function consumeSupportIntent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const flag = sessionStorage.getItem(SUPPORT_INTENT_KEY) === '1';
    sessionStorage.removeItem(SUPPORT_INTENT_KEY);
    return flag;
  } catch {
    return false;
  }
}

// Tournament registration (TournamentDetailModal's "Login/Sign up to
// Register"). Unlike the others, the modal isn't reflected in the URL, so
// landing back on the lobby isn't enough — SaloonLobbyContainer has to look
// the tournament up by ID and re-open the modal itself, which may need a few
// renders once the lobby's live tournament list actually arrives over the
// socket. So this is peek/clear rather than a single atomic consume: the
// caller keeps peeking (and retrying) until the tournament is actually found,
// then explicitly clears it once it has acted.
const TOURNAMENT_INTENT_KEY = 'postLoginTournamentIntent';

export function markTournamentIntentBeforeLogin(tournamentId: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(TOURNAMENT_INTENT_KEY, tournamentId);
  } catch {
    /* noop */
  }
}

export function peekTournamentIntent(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(TOURNAMENT_INTENT_KEY);
  } catch {
    return null;
  }
}

export function clearTournamentIntent(): void {
  try {
    sessionStorage.removeItem(TOURNAMENT_INTENT_KEY);
  } catch {
    /* noop */
  }
}

// Every open table (state.tables) at the moment a guest logs in — not just
// the active one, and not just when leaving from /grid. state.tables is
// plain Redux state — never persisted — so it's always empty on the
// post-login hard reload; without stashing it ourselves, a guest who had
// several tables open (in the grid, OR in the background behind a single
// active table via the header's table switcher) loses all but the one
// consumeLoginReturnPath() already lands them back on. The restore side
// (components/PostLoginTablesRestore) re-subscribes each OTHER table one at
// a time and only clears this once it's actually consumed it.
const OPEN_TABLES_INTENT_KEY = 'postLoginOpenTables';

type StashedOpenTable = { gameID: string; gameType: string };

export type OpenTablesIntent = {
  // True when the guest was viewing /grid (Multi-Table View) at login time —
  // the restore side lands back on /grid once every table is restored,
  // instead of a specific single table.
  wasGridView: boolean;
  // The single table shown at /table/:gameID/:gameType at login time, if any
  // (null when on /grid or the lobby). consumeLoginReturnPath() already
  // lands the guest straight back on this table's own URL via the hard
  // reload, so the restore side excludes it from its own resubscribe loop —
  // it only needs to bring back every OTHER table, silently, in the
  // background.
  activeGameID: string | null;
  tables: StashedOpenTable[];
};

const EMPTY_OPEN_TABLES_INTENT: OpenTablesIntent = {
  wasGridView: false,
  activeGameID: null,
  tables: []
};

function captureOpenTablesBeforeLogin(): void {
  if (typeof window === 'undefined') return;
  try {
    const state = store.getState() as { tables?: Record<string, any> };
    const tables = state?.tables || {};
    const list: StashedOpenTable[] = Object.values(tables)
      .map((table) => ({
        gameID: String(table?.game?.gameID ?? ''),
        gameType: String(table?.game?.game ?? '')
      }))
      .filter((table) => table.gameID && table.gameID !== '0' && table.gameType);
    if (list.length === 0) return;
    const wasGridView = window.location.pathname === '/grid';
    const tableUrlMatch = window.location.pathname.match(/^\/table\/([^/]+)\/[^/]+/);
    const activeGameID = !wasGridView && tableUrlMatch ? tableUrlMatch[1] : null;
    const intent: OpenTablesIntent = { wasGridView, activeGameID, tables: list };
    sessionStorage.setItem(OPEN_TABLES_INTENT_KEY, JSON.stringify(intent));
  } catch {
    /* noop */
  }
}

export function consumeOpenTablesIntent(): OpenTablesIntent {
  if (typeof window === 'undefined') return EMPTY_OPEN_TABLES_INTENT;
  try {
    const raw = sessionStorage.getItem(OPEN_TABLES_INTENT_KEY);
    sessionStorage.removeItem(OPEN_TABLES_INTENT_KEY);
    if (!raw) return EMPTY_OPEN_TABLES_INTENT;
    const parsed = JSON.parse(raw);
    return {
      wasGridView: Boolean(parsed?.wasGridView),
      activeGameID: typeof parsed?.activeGameID === 'string' ? parsed.activeGameID : null,
      tables: Array.isArray(parsed?.tables) ? parsed.tables : []
    };
  } catch {
    return EMPTY_OPEN_TABLES_INTENT;
  }
}

/**
 * Drop any not-yet-consumed post-login state (return path / landing flag /
 * join / seat / support / tournament / open-tables intent) so it can't leak
 * into a different session in the same tab — e.g. a login that was started
 * and abandoned, then a different account logs in later without ever
 * revisiting the original page.
 *
 * Call this from actual logout actions ONLY — never from clearAuth() itself.
 * clearAuth() also runs on every guest page load with no valid token (App.tsx's
 * bootstrap effect), which fires the instant a guest lands on /login, before
 * they've logged in — bundling this in there wiped the return-path stash
 * moments after redirectToLogin() wrote it, before it could ever be read.
 */
export function clearPostLoginState(): void {
  try {
    sessionStorage.removeItem(LOGIN_RETURN_PATH_KEY);
    sessionStorage.removeItem(POST_LOGIN_LANDING_KEY);
    sessionStorage.removeItem(JOIN_INTENT_KEY);
    sessionStorage.removeItem(SEAT_INTENT_KEY);
    sessionStorage.removeItem(SUPPORT_INTENT_KEY);
    sessionStorage.removeItem(TOURNAMENT_INTENT_KEY);
    sessionStorage.removeItem(OPEN_TABLES_INTENT_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Send a visitor to login.
 *
 * - Web: the embedded Descope flow at the in-app `/login` route.
 * - Native: open the auth app's `/loginapp` page in the system browser (the
 *   OS-browser + `hijackpoker://account` deep-link flow that native auth
 *   requires). Going straight to the browser avoids the redundant in-app
 *   "Login to Hijack" screen — the deep-link callback is handled app-wide by
 *   `useNativeLoginDeepLink`.
 *
 *   `/loginapp`, not `/loginmobile`: the two run the same Descope flow and the
 *   same account-status handling, but only `/loginapp` has the biometric
 *   enrollment prompt and unlock screen (apps/auth LoginApp.js). `/loginmobile`
 *   has no biometric code at all, which is why the native apps never offered
 *   Face ID / fingerprint login. `?callback=native` tells the auth app to
 *   finish with the deep link instead of a web handoff.
 */
export function redirectToLogin(): void {
  captureLoginReturnPath();
  captureOpenTablesBeforeLogin();
  if (Capacitor.isNativePlatform()) {
    // Biometric unlock first, browser fallback — see services/biometricLoginEntry.
    // Imported dynamically to break the utils/auth <-> entry import cycle, and
    // fire-and-forget because every failure path inside opens the auth app
    // itself rather than reporting back.
    void import('services/biometricLoginEntry')
      .then((m) => m.startNativeLogin())
      .catch(() => {
        Browser.open({ url: `https://auth.${APP_DOMAIN}/loginapp?callback=native` }).catch(
          () => {}
        );
      });
    return;
  }
  if (typeof window !== 'undefined') {
    window.location.replace(`https://auth.${APP_DOMAIN}/loginapp`);
  }
}

/**
 * Clear all auth state.
 */
export function clearAuth() {
  // No point in a scheduled refresh against a session we're about to nuke.
  stopTokenRefresh();
  // Drop any in-flight refresh promise + flag so a clearAuth landing during
  // an await can't leave `refreshInFlight` stuck true — that would make
  // hasValidAuthToken keep reporting "member" for the full round-trip after
  // the cookie is already gone, and a session-end effect running against
  // that would race with itself.
  refreshInFlight = false;
  refreshInFlightPromise = null;
  Cookies.remove(DESCOPE_COOKIE as string, { path: '/' });
  Cookies.remove(DESCOPE_COOKIE as string, { path: '/', domain: `.${APP_DOMAIN}` });
  // Also try the domain this game client is ACTUALLY running on. APP_DOMAIN
  // (REACT_APP_DOMAIN) is hijack.poker in production — used to reach other
  // backend services, not this app's own hosting domain (hijackpoker.com) —
  // so the line above never matches the cookie storeAuthTokens()/
  // adoptWebLoginHandoff() actually write here. Without this, logout leaves
  // that cookie in place and the very next load (or the reload handleLogout
  // itself triggers) reads it right back in, looking like logout did nothing.
  if (typeof window !== 'undefined') {
    const hostParts = window.location.hostname.split('.');
    const currentApex =
      hostParts.length > 2 ? hostParts.slice(-2).join('.') : window.location.hostname;
    if (currentApex && currentApex !== APP_DOMAIN) {
      Cookies.remove(DESCOPE_COOKIE as string, { path: '/', domain: `.${currentApex}` });
    }
  }
  try {
    // Remove the Descope token AND all cached member identity/state. Clearing
    // only the token left a logged-out user's guest session reading the previous
    // member's playerGUID / name / subscriptions from localStorage (lobby
    // subscribe, waitlist join, etc.), so they could still act as that member.
    localStorage.removeItem(DESCOPE_COOKIE as string);
    localStorage.removeItem('playerGUID');
    localStorage.removeItem('playerName');
    localStorage.removeItem('deviceID');
    localStorage.removeItem('recentlyExitedTables');
    localStorage.removeItem('playerExitedTables');
    localStorage.removeItem('subscribedTables');
    localStorage.removeItem('lastOpenTables');
    localStorage.removeItem('selectedGridTable');
    localStorage.removeItem('subscribedTablesOrder');
    localStorage.removeItem('subscribedTablesOrderOwner');
    localStorage.removeItem('userSession');
    // Member-scoped play state — cleared so a post-logout guest doesn't inherit
    // the previous member's waitlist grace window or buy-back-declined prompts.
    // Keys owned by utils/recentlyLeftWaitlists.ts and utils/buyBackInDeclined.ts.
    localStorage.removeItem('recentlyLeftWaitlists');
    localStorage.removeItem('buyBackInDeclined');
  } catch {
    /* noop */
  }
  // The table cap's copy of the member's Current Tables strip (EN-11131).
  clearShownTableSlots();
  // Wake reactive consumers (useGuest) — for any logout path that clears without
  // a full page reload, this flips the app to guest immediately.
  notifyAuthChange();
}

/**
 * Force a genuinely fresh reload after the session has ended (expired token,
 * explicit logout). `location.reload()` alone doesn't guarantee bypassing the
 * browser's HTTP cache — a permissively-cached index.html/bundle could hand
 * the player right back into the same stale state we just cleared, or into a
 * stale JS bundle if a new one has since deployed. Navigating to a
 * cache-busting URL (a query param no cache entry could have matched) forces
 * a real network fetch. `replace()`, not a normal navigation, so the
 * now-invalid session's page can't be returned to via the back button.
 */
const HARD_RELOAD_PARAM = '_hardReload';

export function hardReload() {
  const url = new URL(window.location.href);
  url.searchParams.set(HARD_RELOAD_PARAM, Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Strip the `_hardReload` param from the address bar once the fresh page it
 * forced has actually loaded. The param's only job is making the navigation
 * target new enough that no cache entry could match it — that fetch has
 * already happened by the time this runs, so the param has nothing left to
 * do, and leaving it visible just makes every post-reload URL look broken.
 * `history.replaceState()` edits the visible URL only — no navigation, no
 * re-fetch, so this can't re-trigger anything hardReload() relies on.
 */
export function cleanUpHardReloadParam(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(HARD_RELOAD_PARAM)) return;
    url.searchParams.delete(HARD_RELOAD_PARAM);
    window.history.replaceState(null, '', url.toString());
  } catch {
    /* noop */
  }
}

/**
 * True exactly on the EDGE where a session that WAS a member just became a
 * guest — never for an ordinary guest's first load (wasMember=false), which
 * must NOT trigger a reload or it would loop forever (see
 * ConnectionManager's isGuestSession() guard for the same class of bug this
 * avoids). This is what App.tsx's isGuest-transition effect reloads on; it's
 * pulled out as a pure function (mirroring resolveAutoBuyInOffer's rationale
 * in components/Waitlist/autoBuyInOffer.ts) so the decision is unit-testable
 * without rendering App.tsx, which isn't tested elsewhere.
 */
export function isSessionEndTransition(wasMember: boolean, isGuestNow: boolean): boolean {
  return wasMember && isGuestNow;
}

/**
 * What the app should do about the stored session on its FIRST mount.
 *
 * `refresh` is the variant this whole change exists for: the session token has
 * expired but the refresh token has not, which on a 1-day/30-day tenant is the
 * state every returning player is in. It must NOT be conflated with `clear`.
 *
 * Extracted as a pure function for the same reason `isSessionEndTransition`
 * was — the bootstrap decision is otherwise only reachable by rendering
 * `App.tsx`, which isn't tested elsewhere, and that untestability is precisely
 * why the cold-start hole survived the EN-10549 fix chain: every individual
 * piece (`startTokenRefresh` fires immediately past exp; `clearAuth` clears
 * what it should) was correct and tested in isolation, while their
 * COMPOSITION at mount was wrong and tested nowhere.
 */
export type BootstrapAction =
  | { kind: 'ok' }
  | { kind: 'refresh' }
  | { kind: 'clear'; reason: 'no-session' | 'refresh-unavailable' };

export function resolveBootstrapAction(input: {
  hasValidToken: boolean;
  canRefresh: boolean;
  hasStoredSession: boolean;
}): BootstrapAction {
  // Usable session token — nothing to do. Note this is also true while a
  // refresh is already in flight (hasValidAuthToken honors refreshInFlight),
  // so a second bootstrap pass can't double-kick the same refresh.
  if (input.hasValidToken) return { kind: 'ok' };

  // Expired session token, live refresh token: recoverable. Spend the refresh
  // token BEFORE deciding this visitor is a guest.
  if (input.canRefresh) return { kind: 'refresh' };

  // Nothing left to spend. Distinguish the two reasons for triage: a plain
  // guest who never logged in ('no-session') looks identical in the UI to a
  // member whose refresh token expired or was repudiated
  // ('refresh-unavailable'), but only the second is ever worth investigating.
  return {
    kind: 'clear',
    reason: input.hasStoredSession ? 'refresh-unavailable' : 'no-session'
  };
}

/**
 * What the bootstrap refresh's outcome means. Mirrors the `resolveBootstrapAction`
 * split so the async half of the decision is testable too.
 *
 * The key invariant, and the reason this isn't just `succeeded ? … : clearAuth()`:
 * a `false` from `refreshAccessToken()` is NOT authoritative for "the session is
 * dead". It is also returned for a 500, a dropped connection, a
 * `REFRESH_CALL_TIMEOUT_MS` timeout, and an undecodable `iss` — and a cold start
 * is exactly when a phone's radio is least likely to be up. Tearing down on a
 * transient failure would log out a player holding a perfectly good 30-day
 * refresh token because their train went into a tunnel as the app launched.
 * Only `sessionTerminallyDead` (E064006 / E064003) is authoritative.
 */
export type BootstrapOutcome = { kind: 'recovered' } | { kind: 'teardown' } | { kind: 'retry' };

export function resolveBootstrapOutcome(input: {
  succeeded: boolean;
  terminallyDead: boolean;
  hasValidToken: boolean;
}): BootstrapOutcome {
  if (input.succeeded) return { kind: 'recovered' };
  if (input.terminallyDead) return { kind: 'teardown' };
  // Anti-race: the scheduler (armed by the same mount) may have landed its own
  // successful refresh while ours was on the wire. Only reachable when the
  // session isn't terminally dead — a repudiated family can't produce a valid
  // token, and preferring teardown there is the deliberate precedence.
  if (input.hasValidToken) return { kind: 'recovered' };
  return { kind: 'retry' };
}
